import type { SpellChecker } from '../types/spell-checker'
import { accentedSpellingOf } from './accented-spelling-of'
import { editDistance } from './edit-distance'

const DEFAULT_SUGGESTION_LIMIT = 5

/** Closest to the typed word first, then A to Z among suggestions the same distance away (SC-12). */
function byDistanceThenAlphabet(word: string) {
  return (a: string, b: string) =>
    editDistance(word, a) - editDistance(word, b) || a.localeCompare(b)
}

/**
 * Suggestions for one word, computed on demand and remembered, ranked by edit distance and then
 * alphabetically. Suggesting is far slower than checking (tens of milliseconds a word against well
 * under a microsecond), so the scan never does it; the UI asks only when the user opens a word.
 */
export function createSuggester(
  checker: SpellChecker,
  limit = DEFAULT_SUGGESTION_LIMIT
): (word: string) => string[] {
  const remembered = new Map<string, string[]>()

  return (word) => {
    const known = remembered.get(word)

    if (known) return known

    const accented = accentedSpellingOf(word)
    const close = [...new Set(checker.suggest(word, limit))]
      .filter((suggestion) => suggestion !== word && suggestion !== accented)
      .sort(byDistanceThenAlphabet(word))
    // The accented spelling of a word that has one goes first, ahead of the ranking (SC-3).
    const suggestions = [...(accented ? [accented] : []), ...close].slice(0, limit)

    remembered.set(word, suggestions)

    return suggestions
  }
}
